import cx from 'classnames';
import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { usePopup } from './popup.context';
import { useForkedRef } from '@dx-ui/utilities-use-forked-ref';
import FocusLock from 'react-focus-lock';
import { useIsClient } from 'usehooks-ts';

export type PopupContentFullscreenPropsBase = {
  popupStyles?: React.CSSProperties;
  isFullscreen?: boolean;
} & React.ComponentProps<'div'>;

/** Used in conjunction with [Popup](?path=/docs/library-components-popup--docs), PopupContent is a component that wraps the content of a popup.
 */
const PopupContentFullscreen = ({
  ref: forwardedRef,
  children,
  className,
  style,
  popupStyles = {},
  ...rest
}: PopupContentFullscreenPropsBase) => {
  const { triggerRef, triggerClickedRef, popoverRef, popupId, isOpen, setOpen, closePopup } =
    usePopup();
  const ref = useForkedRef(forwardedRef, popoverRef);
  const isClient = useIsClient();

  const nonInteractiveElements = useRef<NodeListOf<Element> | undefined>(undefined);

  useEffect(() => {
    const popoverRefCurrent = popoverRef?.current;
    const onMouseDown = (e: MouseEvent) => {
      if (triggerClickedRef.current) {
        triggerClickedRef.current = false;
      } else if (isOpen && popoverRefCurrent && !popoverRefCurrent.contains(e.target as Element)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (isOpen) {
        //parent elements such as <Dialog /> may have event listeners for keydown. Make sure we dont bubble the event back up
        e.stopPropagation();
        switch (e.key) {
          case ' ':
            if (triggerClickedRef.current) {
              triggerClickedRef.current = false;
            } else if (
              isOpen &&
              popoverRefCurrent &&
              !popoverRefCurrent.contains(e.target as Element)
            ) {
              setOpen(false);
            }
            break;
          case 'Escape':
            closePopup();
            break;
          case 'Tab':
            requestAnimationFrame(() => {
              if (popoverRefCurrent && !popoverRefCurrent.contains(document.activeElement)) {
                setOpen(false);
              }
            });
            break;
          default:
            break;
        }
      }
    };
    if (popoverRefCurrent) {
      document.addEventListener('mousedown', onMouseDown);
      document.addEventListener('keydown', onKeyDown);
    }
    if (!isOpen) {
      if (nonInteractiveElements.current) {
        document.body.style.overflow = 'auto';
        nonInteractiveElements.current.forEach((nonInteractiveElement) => {
          if (nonInteractiveElement) {
            nonInteractiveElement.removeAttribute('aria-hidden');
          }
        });
      }
    } else {
      document.body.style.overflow = 'hidden';
      const nonInteractiveElems = document?.querySelectorAll(
        // children of popup element will not have aria-hidden="true" applied
        `#__next *:not(#${popupId}, #${popupId} *):not([aria-hidden="true"])`
      );
      nonInteractiveElems.forEach((nonInteractiveElement) => {
        if (nonInteractiveElement) {
          nonInteractiveElement.setAttribute('aria-hidden', 'true');
        }
      });
      /** interactiveElementWrapper should always be available in the accessibility tree,
       *  the selector above will also set aria-hidden on any parents of the interactiveElementWrapper in between it and element with appWrapperId
       * This function removes aria hidden from the popup's parents */
      let drawerWrapper: Element | null = document?.querySelector(`#${popupId}`);
      const els: Element[] = [];
      while (drawerWrapper) {
        els.push(drawerWrapper);
        drawerWrapper = drawerWrapper.parentElement;
      }
      els?.forEach((navParentElement) => {
        if (navParentElement) {
          navParentElement.removeAttribute('aria-hidden');
        }
      });
      nonInteractiveElements.current = nonInteractiveElems;
    }
    return () => {
      if (popoverRefCurrent) {
        document.removeEventListener('mousedown', onMouseDown);
        document.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [triggerClickedRef, triggerRef, closePopup, isOpen, popoverRef, setOpen, popupId]);

  const content = (
    <>
      {isOpen ? (
        <div
          className="fixed start-0 top-0 z-[100] h-screen w-screen animate-fadein overflow-hidden bg-overlay backdrop-blur-[2px]"
          data-testid="backgroundOverlay"
          onClick={closePopup}
        />
      ) : null}
      <FocusLock disabled={!isOpen}>
        <div
          id={popupId}
          data-testid="popup-content"
          ref={ref}
          className={cx(
            'fixed start-0 top-0 z-[100] max-h-[calc(100vh-80px)] w-full overflow-auto rounded border border-solid border-surface bg-surface shadow-lg',
            {
              block: isOpen,
              hidden: !isOpen,
            },
            className
          )}
          style={{ ...style, ...popupStyles }}
          {...rest}
        >
          {children}
        </div>
      </FocusLock>
    </>
  );

  return isClient ? createPortal(content, document.body) : null;
};

export { PopupContentFullscreen };
export default PopupContentFullscreen;
